import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Version {
  version: string;
  title: string;
  description: string;
  releaseDate: string;
  fileName: string;
  content: string;
}

interface VersionLogProps {
  isOpen: boolean;
  onClose: () => void;
}

const VersionLog: React.FC<VersionLogProps> = ({ isOpen, onClose }) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 获取版本列表
  useEffect(() => {
    if (isOpen) {
      fetchVersions();
    }
  }, [isOpen]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/versions');
      const result = await response.json();
      
      if (result.success) {
        setVersions(result.data);
        // 自动选择最新版本（第一个版本）
        if (result.data && result.data.length > 0) {
          setSelectedVersion(result.data[0]);
        }
      } else {
        setError(result.message || '获取版本列表失败');
      }
    } catch (err) {
      console.error('获取版本列表失败:', err);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 纯Markdown渲染器 - 只处理标准markdown语法
  const renderMarkdownContent = (content: string) => {
    const lines = content.trim().split('\n');
    const elements: React.ReactNode[] = [];
    let key = 0;
    let hasSkippedFirstH1 = false; // 跳过第一个h1标题

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // 空行
      if (line.trim() === '') {
        elements.push(<div key={key++} className="h-3" />);
        continue;
      }

      // 标题
      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={key++} className="text-lg font-semibold text-primary dark:text-primary-light mt-4 mb-2 first:mt-0">
            {line.slice(4)}
          </h3>
        );
        continue;
      }

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={key++} className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-5 mb-3 first:mt-0 border-l-4 border-primary dark:border-primary-light pl-3 bg-gray-50 dark:bg-gray-700/30 py-2 rounded-r">
            {line.slice(3)}
          </h2>
        );
        continue;
      }

      if (line.startsWith('# ')) {
        // 跳过第一个h1标题，因为上面已经显示了
        if (!hasSkippedFirstH1) {
          hasSkippedFirstH1 = true;
          continue;
        }
        elements.push(
          <h1 key={key++} className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-6 mb-4 first:mt-0">
            {line.slice(2)}
          </h1>
        );
        continue;
      }

      // 列表项
      if (line.startsWith('- ')) {
        elements.push(
          <div key={key++} className="flex items-start gap-3 my-1">
            <div className="w-1.5 h-1.5 bg-primary dark:bg-primary-light rounded-full mt-2 flex-shrink-0" />
            <span className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {processInlineMarkdown(line.slice(2))}
            </span>
          </div>
        );
        continue;
      }

      // 分隔线
      if (line.trim() === '---') {
        elements.push(
          <hr key={key++} className="my-6 border-gray-200 dark:border-gray-600" />
        );
        continue;
      }

      // 跳过发布日期行（因为上面已经显示了）
      if (line.trim().startsWith('**发布日期') || line.trim().startsWith('发布日期')) {
        continue;
      }

      // 普通段落
      elements.push(
        <p key={key++} className="text-gray-700 dark:text-gray-300 leading-relaxed my-2">
          {processInlineMarkdown(line)}
        </p>
      );
    }

    return <div className="space-y-1">{elements}</div>;
  };

  // 处理行内markdown格式
  const processInlineMarkdown = (text: string): React.ReactNode => {
    const segments: React.ReactNode[] = [];
    let remaining = text;
    let segmentKey = 0;

    while (remaining.length > 0) {
      // 粗体 **text**
      const boldMatch = remaining.match(/\*\*(.*?)\*\*/);
      if (boldMatch && boldMatch.index !== undefined) {
        // 添加粗体前的文本
        if (boldMatch.index > 0) {
          segments.push(
            <span key={segmentKey++}>
              {remaining.slice(0, boldMatch.index)}
            </span>
          );
        }
        // 添加粗体文本
        segments.push(
          <strong key={segmentKey++} className="font-semibold text-gray-900 dark:text-gray-100">
            {boldMatch[1]}
          </strong>
        );
        remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
        continue;
      }

      // 斜体 *text*
      const italicMatch = remaining.match(/\*([^*]+)\*/);
      if (italicMatch && italicMatch.index !== undefined) {
        if (italicMatch.index > 0) {
          segments.push(
            <span key={segmentKey++}>
              {remaining.slice(0, italicMatch.index)}
            </span>
          );
        }
        segments.push(
          <em key={segmentKey++} className="italic text-gray-600 dark:text-gray-400">
            {italicMatch[1]}
          </em>
        );
        remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
        continue;
      }

      // 内联代码 `code`
      const codeMatch = remaining.match(/`([^`]+)`/);
      if (codeMatch && codeMatch.index !== undefined) {
        if (codeMatch.index > 0) {
          segments.push(
            <span key={segmentKey++}>
              {remaining.slice(0, codeMatch.index)}
            </span>
          );
        }
        segments.push(
          <code key={segmentKey++} className="bg-gray-100 dark:bg-gray-700 text-primary dark:text-primary-light px-1.5 py-0.5 rounded text-sm font-mono">
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.slice(codeMatch.index + codeMatch[0].length);
        continue;
      }

      // 没有更多格式，添加剩余文本
      segments.push(<span key={segmentKey++}>{remaining}</span>);
      break;
    }

    return segments.length === 1 ? segments[0] : <>{segments}</>;
  };

  const handleVersionClick = (version: Version) => {
    setSelectedVersion(version);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden transition-colors duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* 头部 */}
          <div className="relative bg-gradient-to-r from-primary to-primary-light text-white p-6">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all duration-200"
              aria-label="关闭"
            >
              <i className="fas fa-times text-white text-sm"></i>
            </button>
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-1">版本日志</h2>
              <p className="text-white text-opacity-90 text-sm">查看每个版本的更新记录和功能变化</p>
            </div>
          </div>

          <div className="flex h-[calc(90vh-140px)]">
            {/* 左侧版本列表 */}
            <div className="w-1/3 border-r border-gray-200 dark:border-gray-600 overflow-y-auto bg-gray-50 dark:bg-gray-800/50">
              {loading ? (
                <div className="flex items-center justify-center p-12">
                  <div className="flex flex-col items-center space-y-3">
                    <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">加载中...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center p-8">
                  <div className="text-center">
                    <i className="fas fa-exclamation-triangle text-red-400 text-3xl mb-3"></i>
                    <p className="text-red-500 dark:text-red-400 mb-4">{error}</p>
                    <button
                      onClick={fetchVersions}
                      className="px-4 py-2 bg-gradient-to-r from-primary to-primary-light text-white rounded-xl hover:shadow-lg hover:shadow-primary/25 transition-all duration-200"
                    >
                      <i className="fas fa-redo mr-2"></i>
                      重试
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 space-y-2">
                  {versions.map((version, index) => (
                    <motion.div
                      key={version.version}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.3 }}
                      className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                        selectedVersion?.version === version.version
                          ? 'bg-primary/10 dark:bg-primary/20 border-2 border-primary/30 dark:border-primary/40 shadow-sm'
                          : 'bg-white dark:bg-gray-700/80 hover:bg-gray-50 dark:hover:bg-gray-700 border-2 border-transparent hover:border-gray-200 dark:hover:border-gray-500'
                      }`}
                      onClick={() => handleVersionClick(version)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className={`font-semibold text-sm ${
                              selectedVersion?.version === version.version
                                ? 'text-primary dark:text-primary-light'
                                : 'text-gray-900 dark:text-gray-100'
                            }`}>
                              v{version.version}
                            </h3>
                            {index === 0 && (
                              <span className="px-2 py-0.5 bg-gradient-to-r from-green-400 to-emerald-500 text-white text-xs rounded-full font-medium">
                                最新
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 leading-relaxed">
                            {version.title.replace(/^#\s*v[\d.]+\s*-\s*/, '')}
                          </p>
                          {version.releaseDate && (
                            <div className="flex items-center mt-2 text-xs text-gray-500 dark:text-gray-400">
                              <i className="fas fa-calendar-alt mr-1"></i>
                              {version.releaseDate}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* 右侧版本详情 */}
            <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-800">
              {selectedVersion ? (
                <div className="p-8">
                  <motion.div
                    key={selectedVersion.version}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* 版本头部信息 */}
                    <div className="border-b border-gray-200 dark:border-gray-600 pb-6 mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                          v{selectedVersion?.version || ''}
                        </h1>
                        {selectedVersion?.releaseDate && (
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/60 px-3 py-1 rounded-full">
                            <i className="fas fa-calendar-alt mr-2"></i>
                            {selectedVersion.releaseDate}
                          </div>
                        )}
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
                        {selectedVersion?.title?.replace(/^#\s*v[\d.]+\s*-\s*/, '') || ''}
                      </p>
                    </div>

                    {/* 版本内容 */}
                    <div className="max-w-none space-y-1">
                      {renderMarkdownContent(selectedVersion?.content || '')}
                    </div>
                  </motion.div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center"
                  >
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-primary/20 to-primary-light/20 dark:from-primary/30 dark:to-primary-light/30 rounded-full flex items-center justify-center">
                      <i className="fas fa-history text-3xl text-primary dark:text-primary-light"></i>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      选择版本查看详情
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-sm">
                      点击左侧的版本号，查看该版本的详细更新内容和功能变化
                    </p>
                  </motion.div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VersionLog; 